"use strict";

if (!customElements.get("cart-email-gate")) {
  customElements.define(
    "cart-email-gate",
    class CartEmailGate extends ModalDialog {
      static STORAGE_KEY = "cartEmailGateEmail";
      static PENDING_ADD_KEY = "cartEmailGatePendingAdd";
      static memoryEmail = null;

      constructor() {
        super();
        this.form = this.querySelector("form.cart-email-gate-form");
        this.emailInput = this.querySelector("[data-cart-email-input]");
        this.submitButton = this.querySelector("[data-cart-email-submit]");
        this.errorEl = this.querySelector("[data-cart-email-error]");
        this.consentInput = this.querySelector("[data-cart-email-consent]");
        this.moved = true;
      }

      connectedCallback() {
        this.onCustomerFormResult = this.handleCustomerFormResult.bind(this);
        this.onCustomerFormLoading = this.handleCustomerFormLoading.bind(this);
        document.addEventListener("shopify-customer-form:result", this.onCustomerFormResult);
        document.addEventListener("shopify-customer-form:loading", this.onCustomerFormLoading);

        this.consentInput?.addEventListener("change", () => this.clearError());
        this.emailInput?.addEventListener("input", () => this.clearError());

        const closeButton = this.querySelector('[id^="ModalClose-"]');
        if (closeButton) {
          closeButton.addEventListener(
            "click",
            (event) => {
              event.preventDefault();
              this.cancel();
            },
            true,
          );
        }

        if (this.dataset.formErrors === "true") {
          this.show();
          this.emailInput?.focus();
        }

        return super.connectedCallback();
      }

      disconnectedCallback() {
        document.removeEventListener("shopify-customer-form:result", this.onCustomerFormResult);
        document.removeEventListener("shopify-customer-form:loading", this.onCustomerFormLoading);
      }

      handleCustomerFormLoading(event) {
        if (event.detail.form !== this.form) {
          return;
        }

        this.setSubmitLoading(event.detail.loading);
      }

      handleCustomerFormResult(event) {
        if (event.detail.form !== this.form) {
          return;
        }

        const { result, email } = event.detail;
        const strings = window.cartEmailGateStrings || {};

        if (window.isShopifyCustomerFormSuccess?.(result)) {
          CartEmailGate.setStoredEmail(email);
          this.clearError();
          this.hide();
          document.dispatchEvent(new CustomEvent("cart-email-gate-continue"));
          return;
        }

        this.showError(result.error || strings.errorGeneric || "Something went wrong. Please try again.");
      }

      static getStoredEmail() {
        const fromWindow =
          typeof window.cartEmailGateEmail === "string" ? window.cartEmailGateEmail.trim() : "";

        try {
          return (
            sessionStorage.getItem(CartEmailGate.STORAGE_KEY) ||
            fromWindow ||
            CartEmailGate.memoryEmail
          );
        } catch {
          return fromWindow || CartEmailGate.memoryEmail;
        }
      }

      static setStoredEmail(email) {
        const normalized = email?.trim() || "";
        CartEmailGate.memoryEmail = normalized || null;
        window.cartEmailGateEmail = normalized || undefined;

        if (!normalized) {
          return;
        }

        try {
          sessionStorage.setItem(CartEmailGate.STORAGE_KEY, normalized);
        } catch {
          // Tracking prevention may block storage; keep in-memory for this tab.
        }
      }

      static clearPendingAdd() {
        try {
          sessionStorage.removeItem(CartEmailGate.PENDING_ADD_KEY);
        } catch {
          // Ignore storage errors.
        }
      }

      isEnabled() {
        return this.dataset.enabled !== "false";
      }

      hasEmail() {
        return Boolean(CartEmailGate.getStoredEmail());
      }

      getEmail() {
        return CartEmailGate.getStoredEmail();
      }

      openForAddToCart(opener) {
        if (!this.isEnabled() || this.hasEmail()) {
          return false;
        }

        this.openedBy = opener;
        this.clearError();
        if (this.emailInput) {
          this.emailInput.value = "";
        }
        if (this.consentInput) {
          this.consentInput.checked = false;
        }
        this.show(opener);
        this.emailInput?.focus();
        return true;
      }

      cancel() {
        CartEmailGate.clearPendingAdd();
        super.hide();
      }

      clearError() {
        if (this.errorEl) {
          this.errorEl.textContent = "";
          this.errorEl.hidden = true;
        }
      }

      showError(message) {
        if (this.errorEl) {
          this.errorEl.textContent = message;
          this.errorEl.hidden = false;
        }
      }

      setSubmitLoading(loading) {
        if (!this.submitButton) {
          return;
        }

        this.submitButton.toggleAttribute("aria-disabled", loading);
        this.submitButton.classList.toggle("loading", loading);
        this.submitButton.querySelector(".button-overlay-spinner")?.classList.toggle("hidden", !loading);
      }
    },
  );
}
